import React from "react";
import { Route, Switch, Redirect, withRouter } from "react-router-dom";
import "./App.css";
import { Container, Row } from "react-bootstrap";
import NavigationHeader from "./components/common/navigation/NavigationHeader";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import config from "./AzureAdConfig";
import Welcome from "./components/common/signIn/Welcome";
import { loadUserProfile } from "./redux/actions/UserProfileActions";
import { connect } from "react-redux";
import AdminPages from "./components/admin/AdminPages";
import RevSharePages from "./components/revshare/RevSharePages";
import PtoPages from "./components/pto/PtoPages";
import PresClubPages from "./components/presclub/PresClubPages";
import OptionsPages from "./components/options/OptionsPages";
import Version from "./components/common/info/version";
import TravelPages from "./components/travel/TravelPages";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";

class App extends React.Component {

  render() {
    // Redirect to PTO if at root path and authenticated
    if (this.state.isAuthenticated && (window.location.pathname === "/" || window.location.pathname === "")) {
      return <Redirect to="/pto/" />;
    }

    return this.state.isAuthenticated ? (
      <ImpersonationProvider>
        <Container className="app-window min-vh-100 d-flex flex-column" fluid="true">
          <Row>
            <NavigationHeader
              isAdmin={this.props.profile.isAdmin}
              ghost={this.props.profile.ghost}
              logout={this.logout.bind(this)}
              profile={
                this.props.profileIsLoading
                  ? {
                      ...this.props.profile,
                      settings: JSON.parse(localStorage.getItem("settings")),
                    }
                  : this.props.profile
              }
            />
          </Row>

          <Row className="flex-grow-1">
            <Switch>
              <Route exact path="/">
                <Redirect to="/pto/" />
              </Route>
              <Route path="/revshare/" component={RevSharePages} />
              <Route path="/administration/" component={AdminPages} />
              <Route path="/pto/" component={PtoPages} />
              <Route path="/presclub/" component={PresClubPages} />
              <Route path="/options/" component={OptionsPages} />
              <Route path="/travel/" component={TravelPages} />
            </Switch>
          </Row>
          <Row>
            <Version />
          </Row>
        </Container>
      </ImpersonationProvider>
    ) : (
      <Welcome
        {...this.props}
        isAuthenticated={this.state.isAuthenticated}
        user={this.state.user}
        error={this.state.error}
        authButtonMethod={this.login.bind(this)}
      />
    );
  }

  constructor(props) {
    super(props);
    
    // Get the current origin to use as the redirect URI
    const currentOrigin = window.location.origin;
    
    // Create configuration with explicit redirectUri
    const msalConfig = {
      auth: {
        clientId: config.appId,
        authority: config.tenantId,
        redirectUri: currentOrigin,
        postLogoutRedirectUri: currentOrigin,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: true
      },
      // Completely disable logging
      system: {
        loggerOptions: {
          loggerCallback: () => {}, // Empty function to silence all logs
          logLevel: "None", // Disable all logging
          piiLoggingEnabled: false
        }
      }
    };
    
    this.msalInstance = new PublicClientApplication(msalConfig);

    this.state = {
      isAuthenticated: false,
      user: {},
      error: null,
    };

    // Store the current URL if it's not the root
    if (window.location.pathname !== "/" && window.location.pathname !== "") {
      sessionStorage.setItem("loginRedirectUrl", window.location.pathname + window.location.search + window.location.hash);
    }

    this.getUserProfile = this.getUserProfile.bind(this);
  }

  UNSAFE_componentWillMount() {
    this.updateStyle();
  }

  updateStyle() {
    const theme = localStorage.getItem("settings")
      ? JSON.parse(localStorage.getItem("settings")).useDarkTheme
      : true
      ? "darkTheme"
      : "lightTheme";
    const links = document.getElementsByTagName("link");
    for (var i = 0; i < links.length; i++) {
      const linkTag = links[i];
      if (linkTag.title.includes("Theme")) {
        if (linkTag.title === theme) {
          linkTag.disabled = false;
        } else {
          linkTag.disabled = true;
        }
      }
    }
  }

  async login() {
    try {
      // Store the current URL before redirecting
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      sessionStorage.setItem("loginRedirectUrl", currentUrl);
      
      // Set a flag in sessionStorage to track authentication attempts
      const attempts = sessionStorage.getItem("authAttempts") || "0";
      const authAttempts = parseInt(attempts) + 1;
      sessionStorage.setItem("authAttempts", authAttempts.toString());
      
      // Force a simple set of scopes for initial login
      const loginScopes = ["openid", "profile", "email", config.scopes[0]];
      
      // Use redirect login with simplified scopes
      await this.msalInstance.loginRedirect({
        scopes: loginScopes,
        prompt: "select_account"
      });
    } catch (err) {
      this.setState({
        isAuthenticated: false,
        user: {},
        error: {
          message: err.message,
          debug: JSON.stringify(err)
        }
      });
    }
  }

  logout() {
    sessionStorage.clear();
    this.msalInstance.logout();
  }

  async getUserProfile() {
    try {
      // Check for accounts in MSAL
      const accounts = this.msalInstance.getAllAccounts();
      
      if (accounts.length === 0) {
        return;
      }
      
      const currentAccount = accounts[0];
      
      // First, get the access token for EPIIC API silently
      const epiicResponse = await this.msalInstance.acquireTokenSilent({
        scopes: config.scopes,
        account: currentAccount
      });
      
      if (epiicResponse) {
        // Store the token in sessionStorage
        sessionStorage.setItem("accessToken", JSON.stringify(epiicResponse.accessToken));
        
        // After successfully getting the first token, attempt to get the second token
        try {
          const optionsResponse = await this.msalInstance.acquireTokenSilent({
            scopes: config.optionsApiScopes,
            account: currentAccount
          });
          
          if (optionsResponse) {
            // Store the second token
            sessionStorage.setItem("optionsApiToken", JSON.stringify(optionsResponse.accessToken));
          }
        } catch (optionsError) {
          // If getting the options token fails silently, try to get it with interaction
          if (optionsError instanceof InteractionRequiredAuthError) {
            try {
              const optionsResponse = await this.msalInstance.acquireTokenPopup({
                scopes: config.optionsApiScopes,
                account: currentAccount
              });
              
              if (optionsResponse) {
                sessionStorage.setItem("optionsApiToken", JSON.stringify(optionsResponse.accessToken));
              }
            } catch (optionsInteractiveError) {
              // Continue anyway with just the first token
            }
          }
        }
        
        // Check if there's a redirect URL before proceeding
        const redirectUrl = sessionStorage.getItem("loginRedirectUrl");
        
        // Load user profile and update state
        try {
          // Set authenticated state
          this.setState({ 
            user: currentAccount,
            isAuthenticated: true
          }, () => {
            // Load user profile
            this.props.loadUserProfile();
            this.updateStyle();
            
            // Handle redirect AFTER everything is set up
            if (redirectUrl && redirectUrl !== "/" && redirectUrl !== "") {
              // Clean up session storage
              sessionStorage.removeItem("loginRedirectUrl");
              
              // Use React Router to navigate (props.history comes from withRouter)
              if (this.props.history) {
                this.props.history.push(redirectUrl);
              } else {
                // Full page reload as fallback (only if React Router history not available)
                window.location.href = redirectUrl.startsWith('/') ? 
                  window.location.origin + redirectUrl : redirectUrl;
              }
            }
            // Don't do anything for root path - let the render method's Redirect handle it
            // The render method will redirect to /pto/ via <Redirect> if at root path
          });
        } catch (profileError) {
          // Continue with app even if profile load fails
        }
        
        // Reset auth attempts counter
        sessionStorage.removeItem("authAttempts");
      }
    } catch (err) {
      // If this is an InteractionRequiredError, try interactive auth
      if (err instanceof InteractionRequiredAuthError) {
        try {
          await this.msalInstance.acquireTokenRedirect({
            scopes: config.scopes
          });
        } catch (redirectErr) {
          this.setState({
            isAuthenticated: false,
            user: {},
            error: {
              message: redirectErr.message,
              debug: JSON.stringify(redirectErr)
            }
          });
        }
      } else {
        this.setState({
          isAuthenticated: false,
          user: {},
          error: {
            message: err.message,
            debug: JSON.stringify(err)
          }
        });
      }
    }
  }
  
  // Handle the redirect response from Azure AD
  componentDidMount() {
    // Store the current URL BEFORE authentication if it's a meaningful path
    // Don't store root path as it should default to PTO
    if (window.location.pathname !== "/" && 
        window.location.pathname !== "" && 
        !sessionStorage.getItem("loginRedirectUrl")) {
      const currentUrl = window.location.pathname + window.location.search + window.location.hash;
      sessionStorage.setItem("loginRedirectUrl", currentUrl);
    }
    
    // Handle the redirect promise from MSAL
    this.msalInstance.handleRedirectPromise()
      .then(response => {
        if (response) {
          // Authentication successful from redirect
          this.getUserProfile();
        } else {
          // Check if we already have an account
          const accounts = this.msalInstance.getAllAccounts();
          
          if (accounts.length > 0) {
            // User already signed in, getting profile
            this.getUserProfile();
          } else if (!this.state.isAuthenticated) {
            // No account and not authenticated, start login process
            setTimeout(() => {
              this.login();
            }, 500);
          }
        }
      })
      .catch(error => {
        // Check if we have an account anyway
        const accounts = this.msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          this.getUserProfile();
        }
      });
  }
}

function mapStateToProps(state, ownProps) {
  return {
    profile: state.profile,
  };
}

const mapDispatchToProps = {
  loadUserProfile,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));